/**
 * Browser-side client for TikTok Shop order data.
 *
 * No TikTok credential lives here and none ever may: everything VITE_-prefixed
 * is compiled into the public bundle. The app key, app secret, refresh token
 * and shop cipher are Static Web App settings read only by
 * `api/src/functions/tiktokOrders.ts`.
 *
 * Contact details arrive already masked for callers who are not on
 * IGCOM_PII_USERS — the server decides, not this file, so there is nothing
 * here worth tampering with.
 */

import { getApiTokenSilent } from "../../shell/auth/tokens";
import { apiAuthHeaders } from "../../lib/apiAuthHeaders";

export interface TiktokLineItem {
  title: string;
  sku: string;
  /** Always 1 — TikTok sends one line_items entry PER UNIT. */
  quantity: number;
}

export interface TiktokOrder {
  id: string;
  name: string;
  createdAt: string;
  /** TikTok's own state: UNPAID, AWAITING_SHIPMENT, DELIVERED, CANCELLED… */
  status: string;
  total: number;
  currency: string;
  customerKey: string;
  customerName: string;
  customerEmail: string;
  customerPhone: string;
  city: string;
  province: string;
  lineItems: TiktokLineItem[];
}

export interface TiktokSummary {
  orderCount: number;
  revenue: number;
  avgOrderValue: number;
  currency: string;
  units: number;
}

export interface TiktokOverview {
  shop: string;
  asOf: string;
  windowDays: number;
  /** False → names, emails and phones on screen are redacted server-side. */
  piiUnmasked: boolean;
  /** True → more orders exist than the page cap returned. */
  truncated: boolean;
  orders: TiktokOrder[];
  summary: TiktokSummary;
}

/**
 * The message behind a failed response.
 *
 * IT SAYS WHOSE 500 IT WAS, which the first version did not. Our function
 * always answers with `{ error }`, so a 500 carrying no parseable JSON did not
 * come from our code at all — it is the PLATFORM's, and the commonest cause is
 * Static Web Apps killing a request that ran past its ~45s limit. Collapsing
 * both cases into "Request failed (500)." cost an afternoon: the message named
 * nothing, and it looked exactly like a credential problem while the real
 * answer was a walk that needed a clock. Now the raw body comes through, so
 * the two are never the same sentence again.
 */
async function readError(res: Response): Promise<string> {
  const raw = await res.text().catch(() => "");
  try {
    const parsed = JSON.parse(raw) as { error?: string };
    if (parsed.error) return parsed.error;
  } catch {
    /* Not our JSON — fall through and show what did arrive. */
  }
  const snippet = raw.trim().slice(0, 200);
  return snippet
    ? `Request failed (${res.status}). The server did not answer in our own format, which usually `
      + `means the request was cut off rather than refused: ${snippet}`
    : `Request failed (${res.status}) with an empty body — the request was almost certainly cut `
      + `off by the platform's time limit rather than refused by TikTok.`;
}

/**
 * `days = 0` is ALL TIME — the same meaning IG COM gives it.
 *
 * `before`, in unix SECONDS, turns the request into a BACKFILL: orders older
 * than that moment, with no lower bound, so a deep history can be walked
 * across several requests. `days` is ignored when it is set, because a
 * backfill is not a window — it is "keep going from where the last one
 * stopped". The cursor comes from the store (backfillCursor), never from
 * anything remembered here.
 */
export async function fetchTiktokOverview(days = 90, before = 0): Promise<TiktokOverview> {
  const token = await getApiTokenSilent();
  const q = new URLSearchParams({ days: String(days) });
  if (before > 0) q.set("before", String(before));
  const res = await fetch(`/api/tiktok/orders?${q.toString()}`, {
    headers: apiAuthHeaders(token),
  });
  if (!res.ok) throw new Error(await readError(res));
  return (await res.json()) as TiktokOverview;
}

/**
 * Ship an order's packages at TikTok.
 *
 * THERE IS NO UNDO — shipping starts the delivery clock. The caller confirms
 * before calling this; see fulfilActions.
 */
export async function tiktokFulfil(
  orderId: string,
): Promise<{ ok: true; orderId: string; shipped: number; failed: { id: string; error: string }[] }> {
  const token = await getApiTokenSilent();
  const res = await fetch("/api/tiktok/fulfil", {
    method: "POST",
    headers: { ...apiAuthHeaders(token), "content-type": "application/json" },
    body: JSON.stringify({ orderId }),
  });
  if (!res.ok) throw new Error(await readError(res));
  return (await res.json()) as { ok: true; orderId: string; shipped: number; failed: { id: string; error: string }[] };
}

/**
 * A link to the order's shipping label.
 *
 * THE LABEL CARRIES THE BUYER'S REAL NAME AND FULL ADDRESS — it is the one
 * artefact in this module that does, because a courier cannot deliver without
 * them, and it is why this exists: TikTok's order API masks both. The link
 * goes straight to the browser and is stored nowhere. See the endpoint.
 */
export async function tiktokLabelUrl(orderId: string): Promise<string> {
  const token = await getApiTokenSilent();
  const res = await fetch(`/api/tiktok/label?order=${encodeURIComponent(orderId)}`, {
    headers: apiAuthHeaders(token),
  });
  if (!res.ok) throw new Error(await readError(res));
  const { url } = (await res.json()) as { url: string };
  return url;
}
